import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
} from "react-icons/fa";
import {
  getCloudinaryThumbUrl,
  getCloudinaryViewUrl,
} from "../utils/cloudinaryMedia";

/**
 * PhotoCarouselModal
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - items: [{ url, description, date }]
 * - initialIndex: number
 *
 * Lightweight viewer with keyboard support, simple swipe, and single-photo download.
 */

const PhotoCarouselModal = ({
  isOpen,
  onClose,
  items = [],
  initialIndex = 0,
}) => {
  const [index, setIndex] = useState(initialIndex || 0);
  const [loadedViewUrl, setLoadedViewUrl] = useState("");
  const containerRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  useEffect(() => {
    setIndex(initialIndex || 0);
  }, [initialIndex, isOpen]);

  const current = items[index];
  const originalUrl = current?.url;
  const viewUrl = originalUrl ? getCloudinaryViewUrl(originalUrl) : "";
  const thumbUrl = originalUrl ? getCloudinaryThumbUrl(originalUrl) : "";
  const viewLoaded = Boolean(viewUrl) && loadedViewUrl === viewUrl;

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, index, items]);

  useEffect(() => {
    if (!isOpen || !items.length) return;
    const preload = (i) => {
      const item = items[i];
      if (!item?.url) return;
      const img = new Image();
      img.src = getCloudinaryViewUrl(item.url);
    };
    const len = items.length;
    if (len < 2) return;
    preload((index + 1) % len);
    preload((index - 1 + len) % len);
  }, [index, items, isOpen]);

  if (!isOpen) return null;
  if (!items || items.length === 0)
    return createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
        <div className="bg-white p-6 rounded">No photos</div>
      </div>,
      document.body
    );

  const prev = () => setIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
  const next = () => setIndex((i) => (i >= items.length - 1 ? 0 : i + 1));

  const startTouch = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const moveTouch = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const endTouch = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 40) {
      if (diff > 0) next();
      else prev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const downloadCurrent = async () => {
    const url = originalUrl;
    try {
      const res = await fetch(url, { mode: "cors" });
      if (!res.ok) throw new Error("Failed to download");
      const blob = await res.blob();
      const a = document.createElement("a");
      const filename = `photo-${index + 1}.${blob.type.split("/")[1] || "jpg"}`;
      const blobUrl = window.URL.createObjectURL(blob);
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        ref={containerRef}
      >
        <div className="flex items-center justify-between p-3 border-b">
          <div>
            <div className="text-sm text-gray-600">
              {index + 1} / {items.length}
            </div>
            <div className="text-sm font-medium">
              {current?.date ? new Date(current.date).toLocaleString() : ""}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              className="p-2 hover:bg-gray-100 rounded"
              onClick={downloadCurrent}
              title="Download full size"
            >
              <FaDownload />
            </button>
            <button
              className="p-2 hover:bg-gray-100 rounded"
              onClick={onClose}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div
          className="relative flex items-center justify-center bg-black"
          onTouchStart={startTouch}
          onTouchMove={moveTouch}
          onTouchEnd={endTouch}
          style={{ minHeight: 320 }}
        >
          <button
            className="absolute left-2 z-20 p-2 text-white bg-black bg-opacity-30 rounded-full hover:bg-opacity-50"
            onClick={prev}
            aria-label="Previous"
          >
            <FaChevronLeft />
          </button>

          {!viewLoaded && thumbUrl && (
            <img
              src={thumbUrl}
              alt=""
              aria-hidden="true"
              className="max-h-[70vh] object-contain mx-auto"
            />
          )}
          <img
            key={viewUrl}
            src={viewUrl}
            alt={current.description || `Photo ${index + 1}`}
            className={`max-h-[70vh] object-contain mx-auto ${
              viewLoaded ? "" : "absolute opacity-0 pointer-events-none"
            }`}
            onLoad={() => setLoadedViewUrl(viewUrl)}
            ref={(img) => {
              if (img?.complete && img.naturalWidth > 0 && loadedViewUrl !== viewUrl) {
                setLoadedViewUrl(viewUrl);
              }
            }}
            onError={(e) => {
              if (originalUrl && e?.target?.src && e.target.src !== originalUrl) {
                e.target.src = originalUrl;
              } else {
                setLoadedViewUrl(viewUrl);
              }
            }}
          />

          <button
            className="absolute right-2 z-20 p-2 text-white bg-black bg-opacity-30 rounded-full hover:bg-opacity-50"
            onClick={next}
            aria-label="Next"
          >
            <FaChevronRight />
          </button>
        </div>

        <div className="p-4 max-h-48 overflow-auto">
          <div className="whitespace-pre-wrap text-sm text-gray-800">
            {current?.description || "-"}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PhotoCarouselModal;
